if (!customElements.get('collection-grid-carousel')) {
  customElements.define(
    'collection-grid-carousel',
    class CollectionGridCarousel extends HTMLElement {
      constructor() {
        super();
        this.abortController = null;
        this.swiper = null;
        this.onResize = this.onResize.bind(this);
      }

      get prefersReducedMotion() {
        return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      connectedCallback() {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        this.desktopMinWidth = parseInt(this.dataset.desktopMinWidth || '990', 10);
        this.slidesPerView = parseFloat(this.dataset.slidesPerView || '4');
        this.spaceBetween = parseFloat(this.dataset.spaceBetween || '16');
        this.viewport = this.querySelector('.collection-grid-carousel__viewport');
        this.track = this.querySelector('[data-carousel-track]');
        this.prevButton = this.querySelector('[data-carousel-prev]');
        this.nextButton = this.querySelector('[data-carousel-next]');

        if (!this.viewport || !this.track || !this.prevButton || !this.nextButton) return;

        window.addEventListener('resize', this.onResize, {
          passive: true,
          signal: this.abortController.signal
        });

        this.syncState();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.destroySwiper();
      }

      isDesktop() {
        return window.innerWidth >= this.desktopMinWidth;
      }

      applySwiperClasses() {
        this.viewport.classList.add('swiper');
        this.track.classList.add('swiper-wrapper');
        for (const slide of this.track.children) {
          slide.classList.add('swiper-slide');
        }
      }

      removeSwiperClasses() {
        this.viewport.classList.remove('swiper');
        this.track.classList.remove('swiper-wrapper');
        for (const slide of this.track.children) {
          slide.classList.remove('swiper-slide');
          slide.style.width = '';
        }
      }

      initSwiper() {
        if (!window.Swiper || !this.isDesktop()) return;
        if (this.swiper) return;

        this.applySwiperClasses();

        const desktopSlides = Number.isNaN(this.slidesPerView) ? 4 : Math.max(this.slidesPerView, 1);
        const spaceBetween = Number.isNaN(this.spaceBetween) ? 16 : this.spaceBetween;

        this.swiper = new Swiper(this.viewport, {
          slidesPerView: 1,
          spaceBetween,
          breakpoints: {
            768: {
              slidesPerView: 3,
              spaceBetween
            },
            990: {
              slidesPerView: desktopSlides,
              spaceBetween
            }
          },
          allowTouchMove: true,
          speed: this.prefersReducedMotion ? 0 : 300,
          watchOverflow: true,
          navigation: {
            prevEl: this.prevButton,
            nextEl: this.nextButton
          }
        });
      }

      destroySwiper() {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }

        this.removeSwiperClasses();
      }

      syncState() {
        if (!this.isDesktop()) {
          this.destroySwiper();
          this.prevButton.disabled = false;
          this.nextButton.disabled = false;
          return;
        }

        if (!window.Swiper) return;

        if (!this.swiper) {
          this.initSwiper();
          return;
        }

        this.slidesPerView = parseFloat(this.dataset.slidesPerView || '4');
        this.spaceBetween = parseFloat(this.dataset.spaceBetween || '16');

        const desktopSlides = Number.isNaN(this.slidesPerView) ? 4 : Math.max(this.slidesPerView, 1);
        const spaceBetween = Number.isNaN(this.spaceBetween) ? 16 : this.spaceBetween;

        this.swiper.params.spaceBetween = spaceBetween;
        this.swiper.params.breakpoints = {
          768: {
            slidesPerView: 3,
            spaceBetween
          },
          990: {
            slidesPerView: desktopSlides,
            spaceBetween
          }
        };
        this.swiper.params.speed = this.prefersReducedMotion ? 0 : 300;
        this.swiper.update();
      }

      onResize() {
        this.syncState();
      }

      /**
       * Re-apply Swiper slide classes after DOM reorder and reset to first slide.
       * No-op when Swiper is inactive (e.g. mobile).
       */
      refresh() {
        if (!this.swiper || !this.track) {
          return;
        }

        for (const slide of this.track.children) {
          slide.classList.add('swiper-slide');
        }

        this.swiper.update();
        this.swiper.slideTo(0, 0);
      }
    }
  );
}
